import { Building2, GraduationCap, LogOut, Monitor, Moon, Settings, Sun } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/Avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { usePlatformAuth } from '@/features/platform/usePlatformAuth'
import { usePreferences, type Theme } from '@/features/preferences/store'
import { LANGUAGES, type Language } from '@/i18n'
import { APP_NAME } from '@/utils/brand'
import { cn } from '@/utils/cn'

/** The operator console: a slim top bar (no school context) and a centred content column. */
export function PlatformShell() {
  const { t } = useTranslation('platform')
  return (
    <div className="min-h-dvh bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-control focus:bg-surface focus:px-3 focus:py-2"
      >
        {t('Skip to content')}
      </a>
      <header className="sticky top-0 z-30 border-b border-line bg-surface/85 backdrop-blur print:hidden">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
          <NavLink to="/platform" className="flex items-center gap-2.5" aria-label={t('Platform home')}>
            <span className="bg-brand-gradient grid size-9 place-items-center rounded-xl text-white shadow-card">
              <GraduationCap className="size-5" aria-hidden="true" />
            </span>
            <span className="hidden leading-tight sm:block">
              <span className="block text-sm font-semibold text-ink">{APP_NAME}</span>
              <span className="block text-2xs font-medium tracking-wide text-muted uppercase">
                {t('Platform')}
              </span>
            </span>
          </NavLink>
          <nav aria-label={t('Platform')} className="flex items-center gap-1">
            <PlatformNavLink
              to="/platform"
              end
              icon={<Building2 className="size-4" aria-hidden="true" />}
              label={t('Schools')}
            />
            <PlatformNavLink
              to="/platform/settings"
              icon={<Settings className="size-4" aria-hidden="true" />}
              label={t('Settings')}
            />
          </nav>
          <div className="ml-auto">
            <OperatorMenu />
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  )
}

function PlatformNavLink({
  to,
  end,
  icon,
  label,
}: {
  to: string
  end?: boolean
  icon: ReactNode
  label: string
}) {
  const { pathname } = useLocation()
  // "Schools" also covers a school's page.
  const active = end ? pathname === to || pathname.startsWith('/platform/schools') : pathname.startsWith(to)
  return (
    <NavLink
      to={to}
      end={end}
      className={cn(
        'inline-flex items-center gap-2 rounded-control px-3 py-2 text-sm font-medium transition-colors',
        active ? 'bg-brand-50 text-brand-700' : 'text-ink-soft hover:bg-surface-sunken hover:text-ink',
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </NavLink>
  )
}

function OperatorMenu() {
  const { t } = useTranslation()
  const { operator, logout } = usePlatformAuth()
  const { locale, theme, setLocale, setTheme } = usePreferences()
  const navigate = useNavigate()
  if (!operator) return null
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-2.5 rounded-full p-1 transition-colors hover:bg-surface-sunken sm:pr-3"
        aria-label={t('Account menu')}
      >
        <Avatar name={operator.name} size="sm" />
        <span className="hidden max-w-40 truncate text-[0.8125rem] font-semibold text-ink sm:block">
          {operator.name}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="px-2.5 py-2.5">
          <p className="truncate text-sm font-semibold text-ink">{operator.name}</p>
          <p className="truncate text-xs text-muted">{operator.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('Language')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={locale} onValueChange={(v) => setLocale(v as Language)}>
          {LANGUAGES.map((l) => (
            <DropdownMenuRadioItem
              key={l.code}
              value={l.code}
              lang={l.code}
              onSelect={(e) => e.preventDefault()}
            >
              {l.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('Theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as Theme)}>
          <DropdownMenuRadioItem value="light" icon={<Sun />} onSelect={(e) => e.preventDefault()}>
            {t('Light')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" icon={<Moon />} onSelect={(e) => e.preventDefault()}>
            {t('Dark')}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system" icon={<Monitor />} onSelect={(e) => e.preventDefault()}>
            {t('System')}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          icon={<LogOut />}
          tone="danger"
          onSelect={() => void logout().then(() => navigate('/platform/login', { replace: true }))}
        >
          {t('Sign out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
